import { useCallback, useEffect, useRef, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { listPapeisObra } from "./papeis-obra.gateway";
import type { PapeisObraQueryState, PapeisObraSort } from "./papeis-obra.types";

/** Espera entre a digitação e a consulta. */
export const PAPEIS_OBRA_SEARCH_DEBOUNCE_MS = 300;

export const PAPEIS_OBRA_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar os papéis na obra.";
export const PAPEIS_OBRA_ERROR_MESSAGE = "Não foi possível carregar os papéis na obra.";

const INITIAL_STATE: PapeisObraQueryState = {
  status: "loading",
  papeis: [],
  total: 0,
  errorMessage: null,
};

export interface UsePapeisObraParams {
  search: string;
  page: number;
  pageSize: number;
  sort?: PapeisObraSort | null;
}

export function usePapeisObra({ search, page, pageSize, sort = null }: UsePapeisObraParams) {
  const [state, setState] = useState<PapeisObraQueryState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);
  const previousTerm = useRef<string | null>(null);

  const term = search.trim();
  const sortField = sort?.field;
  const sortDirection = sort?.direction;

  useEffect(() => {
    // Debounce só na digitação; carga inicial, paginação, ordenação e retry são imediatos.
    const shouldDebounce = previousTerm.current !== null && previousTerm.current !== term;
    previousTerm.current = term;

    let cancelled = false;
    const controller = new AbortController();

    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        listPapeisObra({
          busca: term,
          page,
          pageSize,
          sort: sortField && sortDirection ? { field: sortField, direction: sortDirection } : null,
          signal: controller.signal,
        })
          .then(({ papeis, total }) => {
            if (cancelled) return;
            setState({ status: "success", papeis, total, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (cancelled) return;

            const isForbidden = isAPIError(error) && error.status === 403;
            setState({
              status: isForbidden ? "forbidden" : "error",
              papeis: [],
              total: 0,
              errorMessage: isForbidden
                ? PAPEIS_OBRA_FORBIDDEN_MESSAGE
                : getErrorMessage(error) || PAPEIS_OBRA_ERROR_MESSAGE,
            });
          });
      },
      shouldDebounce ? PAPEIS_OBRA_SEARCH_DEBOUNCE_MS : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, page, pageSize, sortField, sortDirection, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
